import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Typography, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Stack, Avatar, Tooltip,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import BoltIcon from '@mui/icons-material/Bolt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PaidIcon from '@mui/icons-material/Paid';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer,
  CartesianGrid, Legend, BarChart, Bar,
} from 'recharts';
import PeriodSelector, { initialPeriod } from '../../components/PeriodSelector';
import {
  StatCard, ChartCard, Donut, DonutLegend, areaGradient, trendFromSeries,
  fmtUsd, fmtCompact, CHART_COLORS, ManualRefreshNotice,
} from '../../components/analytics/AnalyticsKit';
import { adminApi } from '../../api/adminApi';

const STATUS_COLOR = { ACTIVE: 'success', SUSPENDED: 'warning', PENDING: 'default', DEACTIVATED: 'error', trial_expired: 'error' };
const PLAN_LABEL_COLOR = { paid: '#1565c0', trial: '#ef6c00' };

/** Warns when the Project cost-allocation tag returns no data (so cost is $0). */
export function TagFilterNotice({ cost }) {
  if (!cost || cost.unavailable || !cost.tagEmpty) { return null; }
  return (
    <Alert severity="info" icon={<LocalOfferIcon fontSize="small" />} sx={{ mb: 2, borderRadius: 2 }}>
      No AWS costs are currently attributed to <code>Project=inventory-platform</code>. Only resources carrying
      that tag are counted — account-wide spend (EC2, ELB, etc.) is intentionally excluded. If you expect costs
      here, activate the <code>Project</code> and <code>Environment</code> tags in Billing → Cost Allocation Tags;
      data appears ~24h after activation and is not retroactive.
    </Alert>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [costs, setCosts] = useState(null);
  const [costPeriod, setCostPeriod] = useState(() => initialPeriod());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      adminApi.getPlatformMetrics('24h'),
      adminApi.listTenants({ limit: 10 }),
    ])
      .then(([m, t]) => { setMetrics(m); setTenants(t.items || []); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Platform costs re-fetch when the selected period changes.
  useEffect(() => {
    let active = true;
    adminApi.getPlatformCosts({ start: costPeriod.start, end: costPeriod.end })
      .then((c) => { if (active) { setCosts(c); } })
      .catch(() => { if (active) { setCosts(null); } });
    return () => { active = false; };
  }, [costPeriod]);

  if (loading) { return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>; }
  if (error) { return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>; }

  const { summary = {}, requestsOverTime = [] } = metrics || {};
  const cost = costs?.cost;
  const costUnavailable = !cost || cost.unavailable;
  const revenue = costs?.revenue;
  const margin = costs?.margin;

  const reqTrend = trendFromSeries(requestsOverTime, 'requests');
  const reqSpark = requestsOverTime.map((r) => ({ v: r.requests }));
  const costByService = costUnavailable ? [] : (cost.services || []).map((s) => ({ name: s.service, value: s.amount }));
  const costTrend = costUnavailable ? [] : (cost.byPeriod || []).map((p) => ({ period: p.start, cost: p.amount }));
  const planData = (summary.byPlan || []).map((p) => ({ name: p.plan, value: p.count }));

  return (
    <Box sx={{ p: 3, maxWidth: 1360 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5} flexWrap="wrap" rowGap={1}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Platform Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Live traffic, tenants, and real AWS cost vs revenue
            {costs?.range ? ` · financials ${costs.range.start} → ${costs.range.end} (${costs.range.days}d)` : ''}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ManualRefreshNotice />
          <PeriodSelector value={costPeriod} onChange={setCostPeriod} />
        </Stack>
      </Stack>
      <Box mt={2}><TagFilterNotice cost={cost} /></Box>

      {/* Headline stats */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Tenants" value={summary.totalTenants ?? '—'}
            sub={`${summary.activeTenants ?? 0} active`} icon={<GroupsIcon />} color="#1565c0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="API Requests (24h)" value={fmtCompact(summary.apiRequests24h ?? 0)}
            trend={reqTrend} spark={reqSpark} icon={<BoltIcon />} color="#00897b" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Error Rate (24h)" value={`${summary.errorRate24h ?? 0}%`}
            sub={summary.avgLatencyMs !== null && summary.avgLatencyMs !== undefined ? `${summary.avgLatencyMs} ms avg latency` : ''}
            icon={<ErrorOutlineIcon />}
            color={(summary.errorRate24h ?? 0) > 1 ? '#c62828' : '#2e7d32'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Monthly Recurring Revenue" value={revenue ? fmtUsd(revenue.monthly) : '—'}
            sub={revenue ? `${revenue.paidTenants} paid tenants` : ''} icon={<PaidIcon />} color="#7b1fa2" />
        </Grid>
      </Grid>

      {/* Traffic + plan mix */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} md={8}>
          <ChartCard title="API Traffic — last 24h" subtitle="Lambda invocations across all platform functions (CloudWatch)">
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={requestsOverTime}>
                <defs>
                  {areaGradient('gReq', '#1565c0')}
                  {areaGradient('gErr', '#c62828')}
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,.08)" />
                <XAxis dataKey="hour" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
                <ChartTooltip />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="requests" name="Requests" stroke="#1565c0" strokeWidth={2}
                  fill="url(#gReq)" dot={false} />
                <Area type="monotone" dataKey="errors" name="Errors" stroke="#c62828" strokeWidth={2}
                  fill="url(#gErr)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Tenants by Plan">
            <Donut data={planData} height={190} valueFormatter={fmtCompact}
              centerValue={summary.totalTenants ?? '—'} centerLabel="tenants"
              colors={planData.map((p) => PLAN_LABEL_COLOR[p.name] || CHART_COLORS[4])} />
            <DonutLegend data={planData} valueFormatter={fmtCompact}
              colors={planData.map((p) => PLAN_LABEL_COLOR[p.name] || CHART_COLORS[4])} />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Financials */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="AWS Cost (actual)"
            value={costUnavailable ? 'N/A' : fmtUsd(cost.total)}
            sub={costUnavailable ? 'Cost Explorer unavailable'
              : (cost.tagEmpty ? 'no tagged cost yet' : 'inventory-platform resources')}
            icon={<CloudQueueIcon />} color="#c62828" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Revenue (period)" value={revenue ? fmtUsd(revenue.period) : '—'}
            sub={revenue ? `${fmtUsd(revenue.monthly)}/mo run-rate` : ''}
            icon={<PaidIcon />} color="#2e7d32" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Net Margin" value={margin ? fmtUsd(margin.amount) : '—'}
            sub={margin && margin.percent !== null ? `${margin.percent}% of revenue` : ''}
            icon={<AccountBalanceIcon />}
            color={margin && margin.amount >= 0 ? '#2e7d32' : '#c62828'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {cost?.byEnvironment?.length ? (
            <ChartCard title="Cost by Environment" sx={{ height: '100%' }}>
              <DonutLegend data={cost.byEnvironment.map((e) => ({ name: e.environment, value: e.amount }))} />
            </ChartCard>
          ) : (
            <StatCard label="Requests Metered (period)" value={fmtCompact(costs?.usage?.apiRequests ?? 0)}
              sub="per-tenant metered requests" icon={<BoltIcon />} color="#5c6bc0" />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={12} md={7}>
          <ChartCard title="AWS Cost Trend"
            subtitle={costUnavailable ? '' : `by ${cost.byPeriod?.length > 3 ? 'period' : 'month'} · Cost Explorer`}>
            {costUnavailable || costTrend.length === 0 ? (
              <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {costUnavailable ? 'AWS cost data unavailable — enable the Cost Explorer API (~24h to populate).' : 'No cost data in this window.'}
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={costTrend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,.08)" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={54}
                    tickFormatter={(v) => `$${v}`} />
                  <ChartTooltip formatter={(v) => fmtUsd(v)} />
                  <Bar dataKey="cost" name="AWS Cost" fill="#1565c0" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <ChartCard title="Cost by Service"
            subtitle={cost?.tagEmpty ? 'no Project-tagged cost yet' : 'tag-filtered to the platform'}>
            {costUnavailable ? (
              <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">No data</Typography>
              </Box>
            ) : (
              <>
                <Donut data={costByService} height={170}
                  centerValue={fmtUsd(cost.total)} centerLabel="total" />
                <DonutLegend data={costByService} />
              </>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      {/* Recent tenants */}
      <ChartCard title="Recent Tenants" subtitle="Revenue vs allocated AWS cost (trailing 30 days)">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Revenue/mo</TableCell>
              <TableCell align="right">AWS Cost (30d)</TableCell>
              <TableCell align="right">Margin</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.map((t) => {
              const m = (t.mrr ?? null) !== null && (t.awsCost ?? null) !== null ? t.mrr - t.awsCost : null;
              return (
                <TableRow key={t.tenantId} hover sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/tenants/${t.tenantId}`)}>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ width: 28, height: 28, fontSize: 13, fontWeight: 700, bgcolor: '#1565c022', color: '#1565c0' }}>
                        {(t.name || '?').slice(0, 1).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.adminEmail}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={t.plan?.toUpperCase()} size="small"
                      sx={{ fontWeight: 700, fontSize: 11 }}
                      color={t.plan === 'paid' ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip label={t.status} size="small" color={STATUS_COLOR[t.status] || 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>
                    {t.mrr !== null && t.mrr !== undefined ? fmtUsd(t.mrr) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, color: 'error.main' }}>
                    {t.awsCost !== null && t.awsCost !== undefined ? fmtUsd(t.awsCost) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: m === null ? 'text.secondary' : (m >= 0 ? 'success.main' : 'error.main') }}>
                    {m === null ? '—' : fmtUsd(m)}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>
                    <Tooltip title={new Date(t.createdAt).toLocaleString()}>
                      <span>{new Date(t.createdAt).toLocaleDateString()}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ChartCard>
    </Box>
  );
}
